import { apiCaller } from "./ApiCaller";
import { getClubId } from "./CalendarBooking";

/**
 * FlowAccount (Thai tax documents) — admin API.
 *
 * Mirrors backend `flowaccountIntegrationRoutes.ts`:
 *   /:club_id/flowaccount-integration/:location_id[...]
 * The backend never returns a secret or token, only whether one exists.
 */

export type FlowAccountPushStatus =
  | "pending"
  | "sent"
  | "failed"
  | "needs_review"
  | "replaced"
  | "voided"
  | "reversal_due"
  | "reversed";

export interface FlowAccountIntegration {
  id: number;
  location_id: number;
  client_id: string;
  environment: "test" | "v1";
  push_documents_enabled: boolean;
  is_vat_registered: boolean;
  bank_account_id: number | null;
  push_pos_sales: boolean;
  push_credit_sales: boolean;
  is_active: boolean;
  is_connected: boolean;
  connected_at: string | null;
  last_sync_at: string | null;
  last_error: string | null;
  last_error_at: string | null;
  document_push_blocked_reason: string | null;
  push_summary: Record<string, number>;
}

export interface FlowAccountCompany {
  compnayName?: string;
  companyName?: string;
  companyNameEn?: string;
  companyTaxId?: string;
  companyBranch?: string;
  companyBranchCode?: string;
  companyAddress?: string;
  [key: string]: unknown;
}

export interface FlowAccountPushRow {
  id: number;
  source_type: string;
  source_id: number;
  reference: string;
  status: FlowAccountPushStatus;
  attempts: number;
  amount: string | number | null;
  flowaccount_document_id: string | null;
  document_serial: string | null;
  last_error: string | null;
  sent_at: string | null;
  created_at: string | null;
}

export interface FlowAccountDocument {
  id: number;
  kind: "abbreviated" | "full";
  document_type: number;
  document_serial: string | null;
  status: FlowAccountPushStatus;
  amount: string | number | null;
  reference: string;
  sent_at: string | null;
  note: string | null;
  replaces_push_id: number | null;
  can_print: boolean;
  can_issue_full: boolean;
}

export interface FlowAccountDocumentsResult {
  enabled: boolean;
  blocked_reason: string | null;
  documents: FlowAccountDocument[];
  issue_status?: string | null;
  issue_message?: string | null;
}

export type FlowAccountSale =
  | { service_booking_id: number }
  | { pos_payment_ids: number[] }
  | { transaction_id: number | null | undefined };

export interface FullTaxInvoiceBuyer {
  name: string;
  tax_id: string;
  branch: string;
  address: string;
  zip_code?: string;
  phone?: string;
  email?: string;
}

const base = (locationId: number) => `${getClubId()}/flowaccount-integration/${locationId}`;

const messageOf = (error: any): string =>
  error?.response?.data?.message || error?.message || "Something went wrong";

const call = async <T>(fn: () => Promise<any>): Promise<T> => {
  try {
    const response = await fn();
    return response?.data?.data as T;
  } catch (error: any) {
    throw messageOf(error);
  }
};

const saleParams = (sale: FlowAccountSale): Record<string, string | number> => {
  if ("service_booking_id" in sale) return { service_booking_id: sale.service_booking_id };
  if ("pos_payment_ids" in sale) return { pos_payment_ids: sale.pos_payment_ids.join(",") };
  return { transaction_id: Number(sale.transaction_id) };
};

/* ── Connection (Location manager) ─────────────────────────────────── */

export const getFlowAccountIntegration = (locationId: number) =>
  call<FlowAccountIntegration | null>(() => apiCaller.get(base(locationId)));

export const connectFlowAccount = (
  locationId: number,
  body: { client_id: string; client_secret: string; environment: "test" | "v1" }
) => call<FlowAccountIntegration>(() => apiCaller.post(`${base(locationId)}/connect`, body));

export const updateFlowAccountIntegration = (locationId: number, patch: Partial<FlowAccountIntegration>) =>
  call<FlowAccountIntegration>(() => apiCaller.patch(base(locationId), patch));

export const testFlowAccountConnection = (locationId: number) =>
  call<{ company: FlowAccountCompany | null }>(() => apiCaller.post(`${base(locationId)}/test`));

export const disconnectFlowAccount = (locationId: number) =>
  call<null>(() => apiCaller.delete(base(locationId)));

export const listFlowAccountPushes = (locationId: number, status?: FlowAccountPushStatus | "") =>
  call<FlowAccountPushRow[]>(() =>
    apiCaller.get(`${base(locationId)}/pushes`, { params: { limit: 100, ...(status ? { status } : {}) } })
  );

export const resolveFlowAccountPush = (
  locationId: number,
  pushId: number,
  body: { action: "retry" | "mark_sent" | "mark_reversed"; flowaccount_document_id?: string }
) => call<null>(() => apiCaller.post(`${base(locationId)}/pushes/${pushId}/resolve`, body));

/* ── Documents at the counter ──────────────────────────────────────── */

export const listSaleDocuments = (locationId: number, sale: FlowAccountSale) =>
  call<FlowAccountDocumentsResult>(() =>
    apiCaller.get(`${base(locationId)}/documents`, { params: saleParams(sale) })
  );

/** Issue whatever slip is missing for the sale (idempotent), then list. */
export const issueSaleDocuments = (locationId: number, sale: FlowAccountSale) =>
  call<FlowAccountDocumentsResult>(() =>
    apiCaller.post(`${base(locationId)}/documents/issue`, saleParams(sale))
  );

export const fetchDocumentPdf = async (
  locationId: number,
  pushId: number,
  culture: "th" | "en"
): Promise<Blob> => {
  try {
    const response = await apiCaller.get(`${base(locationId)}/documents/${pushId}/pdf`, {
      params: { culture },
      responseType: "blob",
    });
    return response.data as Blob;
  } catch (error: any) {
    // A blob error body has to be read before its message is visible.
    const data = error?.response?.data;
    if (data instanceof Blob) {
      try {
        const parsed = JSON.parse(await data.text());
        throw parsed?.message || "Could not load the document";
      } catch (e: any) {
        throw typeof e === "string" ? e : "Could not load the document";
      }
    }
    throw messageOf(error);
  }
};

export const issueFullTaxInvoice = (locationId: number, pushId: number, buyer: FullTaxInvoiceBuyer) =>
  call<{ push_id: number; document_serial: string | null; replaced_status: string }>(() =>
    apiCaller.post(`${base(locationId)}/documents/${pushId}/full-tax-invoice`, buyer)
  );

/**
 * Open a document PDF in a new tab for printing. The tab is opened
 * synchronously (inside the click) so a popup blocker does not eat it.
 */
export const openDocumentPdf = async (locationId: number, pushId: number, culture: "th" | "en") => {
  const tab = window.open("", "_blank");
  try {
    const blob = await fetchDocumentPdf(locationId, pushId, culture);
    const url = URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
    if (tab) tab.location.href = url;
    else window.location.assign(url);
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (error) {
    tab?.close();
    throw error;
  }
};
